import type { BooMapLink, ContainerRow } from "@/lib/api";

// Where a link on the Boos chart leads: one of its two Boos' Sharing pages,
// with the deep-link params SharingTab opens on (see SharingTab.tsx). Pure,
// so `node --test` covers it.
//
//   approved:  the learning Boo, ?source=<sourceId> opens its connection
//              (what it learns, counts, ask for more, remove). Falls back to
//              the sharing Boo, ?review=<linkId> opens what it shares.
//   requested: the Boo that approves, ?review=<linkId> opens the request.
//              Falls back to the asking Boo, ?source=<sourceId> shows where
//              the request stands.
//
// A Boo counts as reachable unless its row says the viewer can't view it.

export type LinkDestination = {
  boo: ContainerRow;
  search: { source: string } | { review: string };
  // What the Sharing page will show, for the confirmation's description.
  description: string;
};

type Side = "consumer" | "source";

export function linkDestination(
  link: BooMapLink,
  boos: ContainerRow[],
): LinkDestination | null {
  const byId = new Map(boos.map((b) => [b.id, b]));
  const consumer = byId.get(link.consumerId);
  const source = byId.get(link.sourceId);
  if (!consumer || !source) return null;
  const reachable = (b: ContainerRow) => b._permissions?.canView !== false;

  const order: Side[] =
    link.status === "requested" ? ["source", "consumer"] : ["consumer", "source"];
  const side = order.find((s) => reachable(s === "consumer" ? consumer : source));
  if (!side) return null;

  // "the sharing page for X", never "X's": names like "Jane's CoS" already end
  // in a possessive.
  const leave = (boo: ContainerRow) => `You'll leave this view and go to the sharing page for ${boo.name}`;
  if (side === "consumer") {
    return {
      boo: consumer,
      search: { source: source.id },
      description:
        link.status === "requested"
          ? `${leave(consumer)} to see where its request to learn from ${source.name} stands.`
          : `${leave(consumer)} to see what it learns from ${source.name}.`,
    };
  }
  return {
    boo: source,
    search: { review: link.id },
    description:
      link.status === "requested"
        ? `${leave(source)} to review the request from ${consumer.name} to learn from it.`
        : `${leave(source)} to see what it shares with ${consumer.name}.`,
  };
}
